import { LegalShell as Layout } from "@/components/connect/LegalShell";
import { Link } from "react-router-dom";
import { ArrowRight, Check, X, Users, Zap, Shield, Phone, Globe, Building2, Mail, Smartphone } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import { Button } from "@/components/ui/button";
import africanFlagsUnity from "@/assets/african-flags-unity.png";
import elefaIcon from "@/assets/elefa-app-icon.webp";
import { SEOHead } from "@/components/seo/SEOHead";
import { MARKETING_CONFIG } from "@/config/marketing";
import { PAGE_SEO, getCanonicalUrl, ORGANIZATION_SCHEMA } from "@/config/seo";

/**
 * About page - truthful posture (May 2026).
 *
 * elefa is an Australian OTT VoIP app for international calling.
 * It is NOT an MVNO. There are no SIMs, no eSIMs, no data plans, no NBN
 * resale and no carrier licence. We are not ACMA-registered. The earlier
 * version of this page made all of those claims and has been rewritten.
 */
const About = () => {
  return (
    <Layout>
      <SEOHead
        title={PAGE_SEO.about.title}
        description={PAGE_SEO.about.description}
        keywords={PAGE_SEO.about.keywords}
        canonicalUrl={getCanonicalUrl('/about')}
        structuredData={ORGANIZATION_SCHEMA}
      />

      {/* Hero */}
      <section className="py-24 lg:py-32">
        <div className="container">
          <div className="mx-auto max-w-3xl text-center">
            <p className="text-sm font-medium text-primary uppercase tracking-wider mb-4">
              About elefa
            </p>
            <h1 className="text-4xl font-semibold text-foreground sm:text-5xl lg:text-6xl mb-6 tracking-tight">
              Affordable international calling, over the internet
            </h1>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto mb-8">
              elefa is an over-the-top Voice-over-IP app built in
              Melbourne. You top up wallet credit, dial any number in the
              world, and pay only for the minutes you use.
            </p>
            <p className="text-lg text-foreground">
              No contracts. No SIM swap. No monthly bill.
            </p>
          </div>
        </div>
      </section>

      {/* What it actually is */}
      <section className="py-16 border-y bg-muted/20">
        <div className="container">
          <div className="mx-auto max-w-4xl">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
              <div>
                <div className="text-3xl font-semibold text-foreground">OTT VoIP</div>
                <div className="text-sm text-muted-foreground mt-1">App-based calling over Wi-Fi or mobile data</div>
              </div>
              <div>
                <div className="text-3xl font-semibold text-foreground">Prepaid</div>
                <div className="text-sm text-muted-foreground mt-1">No bill, no contract, no debt</div>
              </div>
              <div>
                <div className="text-3xl font-semibold text-foreground">195</div>
                <div className="text-sm text-muted-foreground mt-1">Destination countries reachable</div>
              </div>
              <div>
                <div className="text-3xl font-semibold text-foreground">iOS · Android · Web</div>
                <div className="text-sm text-muted-foreground mt-1">Works on the device you already own</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Name */}
      <section className="py-20">
        <div className="container">
          <div className="mx-auto max-w-4xl">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div>
                <p className="text-sm font-medium text-primary uppercase tracking-wider mb-3">
                  Our name
                </p>
                <h2 className="text-3xl font-semibold text-foreground mb-4">
                  Why "elefa"?
                </h2>
                <p className="text-lg text-muted-foreground mb-4">
                  Short for elephant.
                </p>
                <p className="text-foreground mb-4">
                  Elephants stay close to family no matter the distance. We
                  built elefa so anyone - anywhere - can do the same
                  without paying telco prices to do it.
                </p>
              </div>
              <div className="flex justify-center">
                <img
                  src={africanFlagsUnity}
                  alt="People connected across the world"
                  className="w-64 h-64 object-cover rounded-2xl"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why we exist */}
      <section className="py-20 bg-muted/20">
        <div className="container">
          <div className="mx-auto max-w-3xl text-center">
            <p className="text-sm font-medium text-primary uppercase tracking-wider mb-3">
              Why we exist
            </p>
            <h2 className="text-3xl font-semibold text-foreground mb-6">
              Calling home shouldn't cost a fortune
            </h2>
            <p className="text-lg text-muted-foreground mb-6">
              Around the world, diaspora families pay too much to talk to
              loved ones - bundled into mobile bills, prepaid international
              cards or roaming charges. We built a simpler alternative:
              top up a wallet, see the per-minute rate before you dial, and
              pay only for what you use.
            </p>
          </div>
        </div>
      </section>

      {/* Founder */}
      <section className="py-20">
        <div className="container">
          <div className="mx-auto max-w-3xl">
            <div className="text-center mb-12">
              <p className="text-sm font-medium text-primary uppercase tracking-wider mb-3">
                Leadership
              </p>
              <h2 className="text-3xl font-semibold text-foreground">Meet the founder</h2>
            </div>

            <div className="bg-muted/30 border rounded-2xl p-8 md:p-10">
              <div className="flex flex-col md:flex-row gap-8 items-start">
                <div className="flex-shrink-0">
                  <div className="w-20 h-20 rounded-xl bg-foreground flex items-center justify-center">
                    <span className="text-2xl font-semibold text-background">YM</span>
                  </div>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-foreground mb-1">Yol Mayar</h3>
                  <p className="text-primary text-sm font-medium mb-4">Founder &amp; CEO</p>
                  <blockquote className="text-muted-foreground border-l-2 border-border pl-4 mb-4">
                    "For 21+ years I paid too much to call home. Dropped calls,
                    rough audio, surprise charges. I built elefa to
                    solve a problem I lived every day."
                  </blockquote>
                  <p className="text-sm text-muted-foreground">
                    South Sudanese Australian. Melbourne-based.
                  </p>
                </div>
              </div>
            </div>

            <div className="grid md:grid-cols-3 gap-4 mt-6">
              <div className="bg-muted/20 border rounded-xl p-5 text-center">
                <Users className="h-6 w-6 text-muted-foreground mx-auto mb-3" />
                <h3 className="font-medium text-foreground text-sm">Founder &amp; CEO</h3>
                <p className="text-xs text-muted-foreground">Yol Mayar</p>
              </div>
              <div className="bg-muted/20 border rounded-xl p-5 text-center">
                <Building2 className="h-6 w-6 text-muted-foreground mx-auto mb-3" />
                <h3 className="font-medium text-foreground text-sm">Engineering</h3>
                <p className="text-xs text-muted-foreground">Small core team</p>
              </div>
              <div className="bg-muted/20 border rounded-xl p-5 text-center">
                <Zap className="h-6 w-6 text-muted-foreground mx-auto mb-3" />
                <h3 className="font-medium text-foreground text-sm">Operations</h3>
                <p className="text-xs text-muted-foreground">Melbourne, Australia</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How we're different */}
      <section className="py-20 bg-muted/20">
        <div className="container">
          <div className="mx-auto max-w-3xl">
            <div className="text-center mb-12">
              <p className="text-sm font-medium text-primary uppercase tracking-wider mb-3">
                Comparison
              </p>
              <h2 className="text-3xl font-semibold text-foreground mb-3">
                How we compare to traditional international calling
              </h2>
              <p className="text-muted-foreground">
                Same destinations. Simpler model.
              </p>
            </div>

            <div className="bg-background border rounded-xl overflow-hidden">
              <table className="w-full">
                <thead>
                  <tr className="border-b">
                    <th className="text-left p-4 md:p-5 text-sm font-medium text-muted-foreground">Traditional carrier add-on</th>
                    <th className="text-left p-4 md:p-5 text-sm font-medium text-foreground">elefa</th>
                  </tr>
                </thead>
                <tbody className="text-sm">
                  <tr className="border-b">
                    <td className="p-4 md:p-5 text-muted-foreground">
                      <div className="flex items-center gap-2">
                        <X className="h-4 w-4 text-muted-foreground/50 flex-shrink-0" />
                        Monthly bundle whether you use it or not
                      </div>
                    </td>
                    <td className="p-4 md:p-5 text-foreground">
                      <div className="flex items-center gap-2">
                        <Check className="h-4 w-4 text-primary flex-shrink-0" />
                        Pay only for minutes you actually use
                      </div>
                    </td>
                  </tr>
                  <tr className="border-b">
                    <td className="p-4 md:p-5 text-muted-foreground">
                      <div className="flex items-center gap-2">
                        <X className="h-4 w-4 text-muted-foreground/50 flex-shrink-0" />
                        Surprise charges on the bill
                      </div>
                    </td>
                    <td className="p-4 md:p-5 text-foreground">
                      <div className="flex items-center gap-2">
                        <Check className="h-4 w-4 text-primary flex-shrink-0" />
                        Per-minute rate shown before you dial
                      </div>
                    </td>
                  </tr>
                  <tr className="border-b">
                    <td className="p-4 md:p-5 text-muted-foreground">
                      <div className="flex items-center gap-2">
                        <X className="h-4 w-4 text-muted-foreground/50 flex-shrink-0" />
                        Locked to one device or SIM
                      </div>
                    </td>
                    <td className="p-4 md:p-5 text-foreground">
                      <div className="flex items-center gap-2">
                        <Check className="h-4 w-4 text-primary flex-shrink-0" />
                        Works on iOS, Android and web
                      </div>
                    </td>
                  </tr>
                  <tr>
                    <td className="p-4 md:p-5 text-muted-foreground">
                      <div className="flex items-center gap-2">
                        <X className="h-4 w-4 text-muted-foreground/50 flex-shrink-0" />
                        Generic call centre
                      </div>
                    </td>
                    <td className="p-4 md:p-5 text-foreground">
                      <div className="flex items-center gap-2">
                        <Check className="h-4 w-4 text-primary flex-shrink-0" />
                        Built by people who actually call home
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>

      {/* What we are, what we are not */}
      <section className="py-20">
        <div className="container">
          <div className="mx-auto max-w-4xl">
            <div className="text-center mb-12">
              <p className="text-sm font-medium text-primary uppercase tracking-wider mb-3">
                Straight talk
              </p>
              <h2 className="text-3xl font-semibold text-foreground">
                What elefa is - and what it isn't
              </h2>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-background border rounded-xl p-6">
                <Phone className="h-8 w-8 text-primary mb-4" />
                <h3 className="font-medium text-foreground mb-2">What it is</h3>
                <ul className="text-sm text-muted-foreground space-y-2">
                  <li>• An app for outbound calling and messaging over the internet</li>
                  <li>• Prepaid wallet credit, per-minute pricing</li>
                  <li>• Australian-owned and operated</li>
                  <li>• Available at elefa.tel and as native iOS and Android apps.</li>
                </ul>
              </div>
              <div className="bg-background border rounded-xl p-6">
                <Shield className="h-8 w-8 text-muted-foreground mb-4" />
                <h3 className="font-medium text-foreground mb-2">What it isn't</h3>
                <ul className="text-sm text-muted-foreground space-y-2">
                  <li>• Not an MVNO. We do not sell SIMs, eSIMs or data plans</li>
                  <li>• Not a carrier. We do not resell NBN or mobile network access</li>
                  <li>• Not a replacement for your normal phone - cannot reach emergency services (000, 112, 911 etc.)</li>
                  <li>• Not currently a TIO member; not registered with ACMA's CSP register</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Global reach */}
      <section className="py-20 bg-muted/20">
        <div className="container">
          <div className="mx-auto max-w-4xl">
            <div className="text-center mb-12">
              <p className="text-sm font-medium text-primary uppercase tracking-wider mb-3">
                Global reach
              </p>
              <h2 className="text-3xl font-semibold text-foreground">
                Built in Australia, used worldwide
              </h2>
              <p className="text-muted-foreground mt-3 max-w-2xl mx-auto">
                Anyone with internet access can sign up - but local laws on
                Voice-over-IP differ from country to country. Some governments
                restrict or block OTT VoIP apps entirely. We list the major
                ones on our Service Availability page so you know before you
                travel or sign up.
              </p>
            </div>

            <div className="flex justify-center gap-3 flex-wrap">
              <Link to="/legal/service-availability">
                <Button variant="outline" size="sm" className="gap-2">
                  <Globe className="h-4 w-4" />
                  Service availability by country
                </Button>
              </Link>
              <Link to="/regulatory">
                <Button variant="outline" size="sm" className="gap-2">
                  Regulatory information
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Company line */}
      <section className="py-8 border-t">
        <div className="container">
          <div className="mx-auto max-w-3xl text-center">
            <p className="text-xs text-muted-foreground">
              <span className="font-medium text-foreground">{MARKETING_CONFIG.company.legalName}</span>
              {" · "}ACN {MARKETING_CONFIG.company.acn} · ABN {MARKETING_CONFIG.company.abn} · {MARKETING_CONFIG.headquarters}
            </p>
          </div>
        </div>
      </section>

      {/* QR */}
      <section className="py-16">
        <div className="container">
          <div className="mx-auto max-w-sm text-center">
            <div className="border rounded-2xl p-8">
              <div className="flex items-center justify-center gap-2 mb-4">
                <Smartphone className="h-5 w-5 text-muted-foreground" />
                <h3 className="text-lg font-medium text-foreground">Scan to get the app</h3>
              </div>
              <div className="bg-white p-4 rounded-xl inline-block mb-4">
                <QRCodeSVG
                  value="https://elefa.tel/"
                  size={140}
                  level="H"
                  includeMargin={false}
                  imageSettings={{
                    src: elefaIcon,
                    x: undefined,
                    y: undefined,
                    height: 35,
                    width: 35,
                    excavate: true,
                  }}
                />
              </div>
              <p className="text-sm text-muted-foreground">
                elefa - international calling, prepaid
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-foreground py-16">
        <div className="container">
          <div className="mx-auto max-w-2xl text-center">
            <h2 className="mb-4 text-3xl font-semibold text-background">
              Ready to make your first call?
            </h2>
            <p className="mb-8 text-background/60 text-lg">
              Sign up in under a minute. Top up only what you need. Dial home.
            </p>
            <div className="flex flex-wrap justify-center gap-3">
              <Link to="/connect-portal/auth">
                <Button size="lg" variant="secondary" className="gap-2">
                  Create an account
                  <ArrowRight className="h-4 w-4" />
                </Button>
              </Link>
              <Link to="/support">
                <Button size="lg" variant="outline" className="bg-transparent border-background/30 text-background hover:bg-background/10 gap-2">
                  <Mail className="h-4 w-4" />
                  Talk to us
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default About;
